import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"
import { Avatar } from "../../../components/DashboardComponents/AvatarGroup"
import EmptyState from "../../../components/Common/EmptyState"
import MediaFallbackImage from "../../../components/Common/MediaFallbackImage"
import FilterDropdown from "../../../components/Listing/FilterDropdown"
import InfiniteListSentinel from "../../../components/Listing/InfiniteListSentinel"
import useInfinitePagination from "../../../hooks/useInfinitePagination"
import useCallResponseRealtime from "../../../hooks/useCallResponseRealtime"
import useReverseListScroll from "../../../hooks/useReverseListScroll"
import { ICONS } from "../../../constants/icons"
import { get } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import { nameWithYou } from "../../../helpers/userDisplay"
import {
  CALL_RESPONSE_PAGE_SIZE,
  CALL_RESPONSE_SORT_OPTIONS,
  DEFAULT_CALL_RESPONSE_SORT_BY,
  DEFAULT_CALL_RESPONSE_SORT_DIRECTION,
  buildCallResponseThreads,
  compareCallResponseThreads,
  extractCallResponsePage,
  getCallStatusLabel,
  getDetailStatusBadgeClass,
  countCallResponseItems,
  isCallResponseReverseInfinite,
  mapApiCall,
  mergeCallResponseItems,
  patchCallResponseLike,
  removeCallResponseById,
  upsertCallResponseItem,
} from "../../CallsForArtist/callMappers"
import { ADMIN_CALL_DETAIL_WITH } from "./adminInboxData"

const RESPONSE_WITH = ["parent", "user.currentRoles.roleTypes"]
const LIKES_WITH = ["comment", "user"]

const roleToneClass = (badge = "") => {
  const key = String(badge).toLowerCase()
  if (key.includes("freelance")) return "calls-for-artist-detail__role--muted"
  if (key.includes("author")) return "calls-for-artist-detail__role--author"
  return "calls-for-artist-detail__role--warm"
}

const enrichThreadUser = (item, currentUser) => {
  const isMine = Boolean(
    currentUser?.id &&
      item.userId &&
      String(item.userId) === String(currentUser.id),
  )
  return {
    ...item,
    isMine,
    name: nameWithYou(item.name, isMine),
    comments: (item.comments || []).map(child =>
      enrichThreadUser(child, currentUser),
    ),
  }
}

const extractPagedList = payload => {
  const result = payload?.data?.result
  if (Array.isArray(result)) return result
  if (Array.isArray(result?.data)) return result.data
  return []
}

const CallResponseSkeletons = ({ count = 3 }) =>
  Array.from({ length: count }).map((_, index) => (
    <div
      key={`response-skeleton-${index}`}
      className="calls-for-artist-detail__thread calls-for-artist-detail__thread--skeleton"
      aria-hidden="true"
    >
      <div className="amok-skeleton calls-for-artist-detail__skeleton-avatar" />
      <div className="calls-for-artist-detail__skeleton-thread-body">
        <div className="amok-skeleton calls-for-artist-detail__skeleton-line calls-for-artist-detail__skeleton-line--sm" />
        <div className="amok-skeleton calls-for-artist-detail__skeleton-line" />
        <div className="amok-skeleton calls-for-artist-detail__skeleton-line calls-for-artist-detail__skeleton-line--md" />
      </div>
    </div>
  ))

const ResponseActions = ({ item }) => (
  <div className="calls-for-artist-detail__actions-row">
    <button
      type="button"
      className={`calls-for-artist-detail__action calls-for-artist-detail__action--like${
        item.liked ? " calls-for-artist-detail__action--liked" : ""
      }`}
      aria-pressed={item.liked}
      aria-label={item.liked ? "Unlike response" : "Like response"}
      disabled
    >
      <img
        src={item.liked ? ICONS.heartFill : ICONS.heartOutlineRed}
        alt=""
        width={14}
        height={14}
      />
      <span>{item.likes || 0}</span>
    </button>
    <span className="calls-for-artist-detail__action calls-for-artist-detail__action--comments">
      <img src={ICONS.messageText} alt="" width={14} height={14} />
      <span>{item.replies || 0}</span>
    </span>
    <button
      type="button"
      className="calls-for-artist-detail__reply-link"
      disabled
    >
      Reply
    </button>
  </div>
)

const BACK_PATH = "/admin/inbox"

const AdminInboxCallDetail = () => {
  const { callId } = useParams()
  const navigate = useNavigate()
  const currentUser = useSelector(state => state.auth.currentUser)
  const [call, setCall] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [rawResponses, setRawResponses] = useState([])
  const [likes, setLikes] = useState([])
  const [isResponsesLoading, setIsResponsesLoading] = useState(false)
  const [isLoadingMoreResponses, setIsLoadingMoreResponses] = useState(false)
  const [responsePage, setResponsePage] = useState(1)
  const [hasMoreResponses, setHasMoreResponses] = useState(false)
  const [responseTotal, setResponseTotal] = useState(0)
  const [sortBy, setSortBy] = useState(DEFAULT_CALL_RESPONSE_SORT_BY)
  const [sortDirection, setSortDirection] = useState(
    DEFAULT_CALL_RESPONSE_SORT_DIRECTION,
  )
  const threadsRef = useRef(null)
  const rawResponsesRef = useRef([])
  const markPrependRef = useRef(() => {})
  const currentUserId =
    currentUser?.id != null ? String(currentUser.id) : ""

  rawResponsesRef.current = rawResponses

  document.title = "Call Detail | AMOK . EARTH Admin"

  const fetchCall = useCallback(async () => {
    if (!callId) {
      setCall(null)
      setError("Call not found")
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await get(endpoints.getAdminCallById(callId), {
        params: { "with[]": ADMIN_CALL_DETAIL_WITH },
      })

      if (response?.data?.success) {
        setCall(mapApiCall(response.data.result || {}))
      } else {
        const message = toastApiError(response, "Failed to load call")
        setError(message)
        setCall(null)
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load call")
      setError(message)
      setCall(null)
    } finally {
      setIsLoading(false)
    }
  }, [callId])

  const fetchResponses = useCallback(
    async ({ silent = false, page: pageToLoad = 1, append = false } = {}) => {
      if (!callId) {
        setRawResponses([])
        setHasMoreResponses(false)
        setResponseTotal(0)
        return
      }

      if (append) {
        setIsLoadingMoreResponses(true)
      } else if (!silent) {
        setIsResponsesLoading(true)
      }

      try {
        const requests = [
          get(endpoints.getCallResponses(callId), {
            params: {
              page: pageToLoad,
              per_page: CALL_RESPONSE_PAGE_SIZE,
              sort_by: sortBy,
              sort_direction: sortDirection,
              "with[]": RESPONSE_WITH,
            },
          }),
        ]

        if (!append) {
          requests.push(
            get(endpoints.getCallLikes(callId), {
              params: {
                page: 1,
                per_page: CALL_RESPONSE_PAGE_SIZE,
                "with[]": LIKES_WITH,
              },
            }),
          )
        }

        const [responsesRes, likesRes] = await Promise.all(requests)

        if (!responsesRes?.data?.success) {
          toastApiError(responsesRes, "Failed to load responses")
          if (!append) {
            setRawResponses([])
            setHasMoreResponses(false)
            setResponseTotal(0)
          }
          return
        }

        if (likesRes && !likesRes?.data?.success) {
          toastApiError(likesRes, "Failed to load response likes")
        }

        const { list, currentPage, lastPage, total } =
          extractCallResponsePage(responsesRes)

        if (!append && likesRes?.data?.success) {
          setLikes(extractPagedList(likesRes))
        }

        if (
          append &&
          isCallResponseReverseInfinite(sortBy, sortDirection)
        ) {
          markPrependRef.current?.()
        }

        setRawResponses(prev =>
          append ? mergeCallResponseItems(prev, list) : list,
        )
        setResponsePage(currentPage)
        setHasMoreResponses(currentPage < lastPage)
        setResponseTotal(
          Number.isFinite(total) ? Math.max(0, total) : list.length,
        )
      } catch (err) {
        toastApiError(err, "Failed to load responses")
        if (!append) {
          setRawResponses([])
          setHasMoreResponses(false)
          setResponseTotal(0)
        }
      } finally {
        setIsResponsesLoading(false)
        setIsLoadingMoreResponses(false)
      }
    },
    [callId, sortBy, sortDirection],
  )

  useEffect(() => {
    fetchCall()
  }, [fetchCall])

  useEffect(() => {
    if (!callId) return undefined
    fetchResponses({ page: 1, append: false })
    return undefined
  }, [callId, fetchResponses])

  const responses = useMemo(() => {
    const authorUserId =
      call?.raw?.user_id || call?.raw?.user?.id || call?.postedBy?.id || null
    return buildCallResponseThreads({
      responses: rawResponses,
      likes,
      authorUserId,
      callId,
    }).map(item => enrichThreadUser(item, currentUser))
  }, [
    rawResponses,
    likes,
    call?.raw?.user_id,
    call?.raw?.user?.id,
    call?.postedBy?.id,
    callId,
    currentUser,
  ])

  const sortedResponses = useMemo(
    () =>
      [...responses].sort((a, b) =>
        compareCallResponseThreads(a, b, sortBy, sortDirection),
      ),
    [responses, sortBy, sortDirection],
  )

  const isReverseInfinite = isCallResponseReverseInfinite(
    sortBy,
    sortDirection,
  )
  const displayedResponses = isReverseInfinite
    ? [...sortedResponses].reverse()
    : sortedResponses

  const { markPrepend } = useReverseListScroll({
    enabled: isReverseInfinite && !isResponsesLoading,
    containerRef: threadsRef,
    itemsKey: `${callId}-${displayedResponses.length}`,
    isLoading: isResponsesLoading || isLoadingMoreResponses,
    resetKey: `${callId}-${sortBy}-${sortDirection}`,
  })
  markPrependRef.current = markPrepend

  const loadMoreResponses = useCallback(() => {
    if (!hasMoreResponses || isResponsesLoading || isLoadingMoreResponses) {
      return
    }
    fetchResponses({
      page: responsePage + 1,
      append: true,
      silent: true,
    })
  }, [
    fetchResponses,
    hasMoreResponses,
    isResponsesLoading,
    isLoadingMoreResponses,
    responsePage,
  ])

  const { sentinelRef } = useInfinitePagination({
    hasMore: hasMoreResponses && !isResponsesLoading,
    isLoading: isLoadingMoreResponses,
    onLoadMore: loadMoreResponses,
    resetKey: `${callId}-${displayedResponses.length}`,
  })

  const postedBy = call?.postedBy || { name: "N/A", avatar: null }
  const postedByIsYou = Boolean(
    currentUserId &&
      postedBy?.id &&
      String(postedBy.id) === currentUserId,
  )
  const postedByDisplayName = nameWithYou(postedBy.name, postedByIsYou)
  const projectCode = call?.projectCode || "N/A"
  const responseCount = Math.max(
    0,
    Number(responseTotal) || Number(call?.responses) || 0,
  )

  const handleRealtimeCreated = useCallback(response => {
    if (!response?.id && response?.id !== 0) return
    const id = String(response.id)
    const alreadyExists = rawResponsesRef.current.some(
      item => String(item?.id) === id,
    )
    setRawResponses(prev => upsertCallResponseItem(prev, response))
    if (alreadyExists) return
    setResponseTotal(prev => Math.max(0, Number(prev || 0) + 1))
    setCall(prev =>
      prev
        ? {
            ...prev,
            responses: Math.max(0, Number(prev.responses || 0) + 1),
          }
        : prev,
    )
  }, [])

  const handleRealtimeUpdated = useCallback(response => {
    if (!response?.id && response?.id !== 0) return
    setRawResponses(prev =>
      upsertCallResponseItem(prev, response, { replace: true }),
    )
  }, [])

  const handleRealtimeDeleted = useCallback(
    responseId => {
      if (responseId == null || responseId === "") return
      const prev = rawResponsesRef.current
      const before = countCallResponseItems(prev)
      const next = removeCallResponseById(prev, responseId)
      const removedCount = before - countCallResponseItems(next)
      if (removedCount <= 0) return

      setRawResponses(next)
      setResponseTotal(total => Math.max(0, Number(total || 0) - removedCount))
      setCall(callPrev =>
        callPrev
          ? {
              ...callPrev,
              responses: Math.max(
                0,
                Number(callPrev.responses || 0) - removedCount,
              ),
            }
          : callPrev,
      )
    },
    [],
  )

  const handleRealtimeLike = useCallback(
    ({ commentId, likesCount, userId, removed = false } = {}) => {
      if (commentId == null || commentId === "") return
      const isCurrentUser =
        currentUserId && userId && String(userId) === currentUserId
      setRawResponses(prev => {
        const current = prev.find(
          item => String(item?.id) === String(commentId),
        )
        const nextLikes =
          likesCount != null
            ? likesCount
            : Math.max(
                0,
                Number(current?.likes_count || 0) + (removed ? -1 : 1),
              )
        return patchCallResponseLike(prev, commentId, {
          likes: nextLikes,
          liked: isCurrentUser ? !removed : undefined,
        })
      })
    },
    [currentUserId],
  )

  useCallResponseRealtime({
    callId,
    enabled: Boolean(callId),
    onCreated: handleRealtimeCreated,
    onUpdated: handleRealtimeUpdated,
    onDeleted: handleRealtimeDeleted,
    onLike: handleRealtimeLike,
  })

  return (
    <div className="admin-page admin-inbox-call-detail">

        <button
          type="button"
          className="calls-for-artist-detail__back"
          onClick={() => navigate(BACK_PATH)}
        >
          <img src={ICONS.arrowLeft} alt="" width={16} height={16} />
          Back to Inbox & Calls
        </button>

        {isLoading ? (
          <article
            className="calls-for-artist-detail__hero calls-for-artist-detail__hero--skeleton"
            aria-busy="true"
            aria-hidden="true"
          >
            <div className="amok-skeleton calls-for-artist-detail__skeleton-poster" />
            <div className="calls-for-artist-detail__skeleton-body">
              <div className="amok-skeleton calls-for-artist-detail__skeleton-line calls-for-artist-detail__skeleton-line--title" />
              <div className="amok-skeleton calls-for-artist-detail__skeleton-line" />
              <div className="amok-skeleton calls-for-artist-detail__skeleton-line calls-for-artist-detail__skeleton-line--md" />
              <div className="amok-skeleton calls-for-artist-detail__skeleton-line calls-for-artist-detail__skeleton-line--sm" />
            </div>
          </article>
        ) : null}

        {!isLoading && !call ? (
          <EmptyState
            title="Call not found"
            description={error || "This call could not be loaded."}
            actionText="Back to Inbox & Calls"
            onActionClick={() => navigate(BACK_PATH)}
          />
        ) : null}

        {!isLoading && call ? (
          <>
            <article className="calls-for-artist-detail__hero">
              <MediaFallbackImage
                src={call.image}
                fallback={ICONS.thumbnailPlaceholder}
                alt=""
                className="calls-for-artist-detail__poster"
              />

              <div className="calls-for-artist-detail__body">
                <div className="calls-for-artist-detail__title-row">
                  <h1 className="calls-for-artist-detail__title">
                    {call.headline || "N/A"}
                  </h1>

                  <div className="calls-for-artist-detail__status-actions">
                    <span
                      className={`calls-for-artist-detail__badge ${getDetailStatusBadgeClass(
                        call,
                      )}`}
                    >
                      {getCallStatusLabel(call)}
                    </span>
                    {call.deadline ? (
                      <span className="calls-for-artist-detail__deadline">
                        {call.deadline}
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="calls-for-artist-detail__bottom">
                  <p className="calls-for-artist-detail__desc">
                    {call.description}
                  </p>

                  <div className="calls-for-artist-detail__lower">
                  <div className="calls-for-artist-detail__copy">
                    <div className="calls-for-artist-detail__meta">
                      <span className="calls-for-artist-detail__location">
                        <img
                          src={ICONS.callLocationGreen}
                          alt=""
                          width={15}
                          height={15}
                        />
                        {call.city}
                      </span>
                      {(call.categoryNames || []).map(name => (
                        <span
                          key={name}
                          className="calls-for-artist-detail__category"
                        >
                          {name}
                        </span>
                      ))}
                      <span className="calls-for-artist-detail__posted">
                        Posted {call.posted}
                      </span>
                      <span
                        className="calls-for-artist-detail__dot"
                        aria-hidden="true"
                      >
                        •
                      </span>
                      <span className="calls-for-artist-detail__count">
                        {responseCount} responses
                      </span>
                    </div>
                  </div>

                  <div className="calls-for-artist-detail__entities">
                    <div className="calls-for-artist-detail__entity">
                      <span
                        className="calls-for-artist-detail__entity-code"
                        aria-hidden="true"
                      >
                        {projectCode}
                      </span>
                      <span className="calls-for-artist-detail__entity-copy">
                        <span className="calls-for-artist-detail__entity-label">
                          Project:
                        </span>
                        <span className="calls-for-artist-detail__entity-value">
                          {call.projectName || call.title}
                        </span>
                      </span>
                    </div>

                    <div className="calls-for-artist-detail__entity">
                      <Avatar
                        src={postedBy.avatar}
                        name={postedBy.name}
                        size="sm"
                        className="calls-for-artist-detail__entity-avatar"
                      />
                      <span className="calls-for-artist-detail__entity-copy">
                        <span className="calls-for-artist-detail__entity-label">
                          Posted by:
                        </span>
                        <span className="calls-for-artist-detail__entity-value">
                          {postedByDisplayName}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
                </div>
              </div>
            </article>

            <div className="calls-for-artist-detail__section-head">
              <h2 className="calls-for-artist-detail__section-title">
                {responseCount} Responses
              </h2>

              <FilterDropdown
                label="Sort by"
                className="calls-for-artist-detail__sort-filter"
                filters={[
                  {
                    key: "sortBy",
                    type: "sortBy",
                    label: "Sort By",
                    value: sortBy,
                    defaultValue: DEFAULT_CALL_RESPONSE_SORT_BY,
                    onChange: next =>
                      setSortBy(next || DEFAULT_CALL_RESPONSE_SORT_BY),
                    options: CALL_RESPONSE_SORT_OPTIONS,
                  },
                  {
                    key: "sortDirection",
                    type: "sortDirection",
                    label: "Direction",
                    value: sortDirection,
                    defaultValue: DEFAULT_CALL_RESPONSE_SORT_DIRECTION,
                    onChange: next =>
                      setSortDirection(
                        next || DEFAULT_CALL_RESPONSE_SORT_DIRECTION,
                      ),
                  },
                ]}
              />
            </div>

            <div
              ref={threadsRef}
              className="calls-for-artist-detail__threads"
              aria-busy={
                isResponsesLoading || isLoadingMoreResponses || undefined
              }
            >
              {isReverseInfinite ? (
                <>
                  <InfiniteListSentinel
                    sentinelRef={sentinelRef}
                    hasMore={hasMoreResponses && !isResponsesLoading}
                  />
                  {isLoadingMoreResponses ? (
                    <CallResponseSkeletons count={2} />
                  ) : null}
                </>
              ) : null}

              {isResponsesLoading ? <CallResponseSkeletons /> : null}

              {!isResponsesLoading &&
                displayedResponses.map(response => {
                  const comments = response.comments || []

                  return (
                    <article
                      key={response.id}
                      className="calls-for-artist-detail__thread"
                    >
                      <Avatar
                        src={response.avatar}
                        name={response.name}
                        size="md"
                        className="calls-for-artist-detail__thread-avatar"
                      />

                      <div className="calls-for-artist-detail__thread-body">
                        <div className="calls-for-artist-detail__thread-meta">
                          <span className="calls-for-artist-detail__thread-name">
                            {response.name || "N/A"}
                          </span>
                          <span
                            className={`calls-for-artist-detail__role ${roleToneClass(
                              response.badge,
                            )}`}
                          >
                            {response.badge || "N/A"}
                          </span>
                          <span className="calls-for-artist-detail__thread-time">
                            {response.time || "N/A"}
                          </span>
                        </div>

                        <p className="calls-for-artist-detail__thread-text">
                          {response.text || "N/A"}
                        </p>

                        {response.attachment ? (
                          <div className="calls-for-artist-detail__file">
                            <img
                              src={ICONS.callUploadExport}
                              alt=""
                              width={16}
                              height={16}
                            />
                            <span>
                              {response.attachment.name || "N/A"}
                              {response.attachment.size
                                ? ` · ${response.attachment.size}`
                                : ""}
                            </span>
                          </div>
                        ) : null}

                        <ResponseActions item={response} />

                        {comments.length > 0 ? (
                          <div className="calls-for-artist-detail__replies">
                            {comments.map(comment => (
                              <div
                                key={comment.id}
                                className="calls-for-artist-detail__reply-card"
                              >
                                <div className="calls-for-artist-detail__reply-head">
                                  <Avatar
                                    src={comment.avatar}
                                    name={comment.name}
                                    size="xs"
                                  />
                                  <div className="calls-for-artist-detail__reply-meta">
                                    <div className="calls-for-artist-detail__reply-names">
                                      <span>{comment.name || "N/A"}</span>
                                      {comment.author ? (
                                        <span className="calls-for-artist-detail__author">
                                          Author
                                        </span>
                                      ) : null}
                                    </div>
                                    <span className="calls-for-artist-detail__thread-time">
                                      {comment.time || "N/A"}
                                    </span>
                                  </div>
                                </div>
                                <p className="calls-for-artist-detail__reply-text">
                                  {comment.text || "N/A"}
                                </p>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </article>
                  )
                })}

              {!isReverseInfinite && isLoadingMoreResponses ? (
                <CallResponseSkeletons count={2} />
              ) : null}

              {!isReverseInfinite ? (
                <InfiniteListSentinel
                  sentinelRef={sentinelRef}
                  hasMore={hasMoreResponses && !isResponsesLoading}
                />
              ) : null}

              {!isResponsesLoading &&
              !isLoadingMoreResponses &&
              sortedResponses.length === 0 ? (
                <EmptyState
                  title="No responses yet"
                  description="When artists respond to this call, they’ll show up here."
                />
              ) : null}
            </div>
          </>
        ) : null}
    </div>
  )
}

export default AdminInboxCallDetail
